import { useState, useEffect, useCallback } from 'react';
import ShoppingListItem  from './ShoppingListItem';
import PriceModeSelector from './PriceModeSelector';
import PriceSummaryFooter from './PriceSummaryFooter';
import StoreGroupingView  from './StoreGroupingView';
import AddItemBar         from './AddItemBar';
import ReceiptScanner     from './ReceiptScanner';
import ProductPhotoCapture from './ProductPhotoCapture';
import { BACKEND_ENABLED } from '../../config';
import { matchPrices } from '../../utils/priceMatching';

export default function ShoppingListView({ list, onUpdate }) {
  const [priceMode,     setPriceMode]     = useState('lowest');
  const [showGrouping,  setShowGrouping]  = useState(false);
  const [prices,        setPrices]        = useState({});  // { itemName: { Coles, Woolworths, Aldi } }
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showReceipt,   setShowReceipt]   = useState(false);
  const [showPhoto,     setShowPhoto]     = useState(false);

  const items = list.items || [];

  // Match item names against the static deals snapshot whenever item names change
  const fetchPrices = useCallback(async (itemList) => {
    if (!itemList.length) { setPrices({}); return; }
    setLoadingPrices(true);
    try {
      const res = await fetch('/data/deals.json', { cache: 'no-store' });
      const data = await res.json();
      setPrices(matchPrices(itemList.map(i => i.name), data.deals || []));
    } catch (_) {
      // silently ignore price fetch errors — items still usable
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items.map(i => i.name))]);

  function addItems(newItems) {
    onUpdate({ ...list, items: [...items, ...newItems] });
  }

  function toggleCheck(id) {
    onUpdate({
      ...list,
      items: items.map(i => i.id === id ? { ...i, is_checked: !i.is_checked } : i),
    });
  }

  function removeItem(id) {
    onUpdate({ ...list, items: items.filter(i => i.id !== id) });
  }

  function clearChecked() {
    onUpdate({ ...list, items: items.filter(i => !i.is_checked) });
  }

  const checkedCount = items.filter(i => i.is_checked).length;
  const uncheckedItems = items.filter(i => !i.is_checked);
  const checkedItems   = items.filter(i => i.is_checked);

  return (
    <div className="slv-wrap">
      <AddItemBar
        onAdd={addItems}
        onOpenReceipt={() => setShowReceipt(true)}
        onOpenPhoto={() => setShowPhoto(true)}
      />

      {items.length > 0 && (
        <PriceModeSelector mode={priceMode} onChange={setPriceMode} />
      )}

      {items.length > 0 && (
        <div className="slv-view-toggle">
          <button
            className={`slv-toggle-btn ${!showGrouping ? 'slv-toggle-btn--active' : ''}`}
            onClick={() => setShowGrouping(false)}
          >
            ☑ List
          </button>
          <button
            className={`slv-toggle-btn ${showGrouping ? 'slv-toggle-btn--active' : ''}`}
            onClick={() => setShowGrouping(true)}
          >
            🏪 By Store
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="slv-empty">
          <p className="slv-empty-icon">🛒</p>
          <p className="slv-empty-title">Your list is empty</p>
          <p className="slv-empty-sub">Type an item above, scan a receipt, or take a product photo</p>
        </div>
      ) : showGrouping ? (
        <StoreGroupingView
          items={items}
          prices={Object.fromEntries(items.map(i => [i.name, prices[i.name]]))}
        />
      ) : (
        <ul className="slv-list">
          {uncheckedItems.map(item => (
            <li key={item.id}>
              <ShoppingListItem
                item={item}
                prices={loadingPrices ? undefined : prices[item.name]}
                priceMode={priceMode}
                onToggleCheck={toggleCheck}
                onRemove={removeItem}
              />
            </li>
          ))}

          {checkedItems.length > 0 && (
            <>
              <li className="slv-divider">
                <span>In cart ({checkedCount})</span>
                <button className="slv-clear-btn" onClick={clearChecked}>Clear</button>
              </li>
              {checkedItems.map(item => (
                <li key={item.id}>
                  <ShoppingListItem
                    item={item}
                    prices={loadingPrices ? undefined : prices[item.name]}
                    priceMode={priceMode}
                    onToggleCheck={toggleCheck}
                    onRemove={removeItem}
                  />
                </li>
              ))}
            </>
          )}
        </ul>
      )}

      {items.length > 0 && !showGrouping && (
        <PriceSummaryFooter
          items={uncheckedItems}
          prices={prices}
          priceMode={priceMode}
        />
      )}

      {BACKEND_ENABLED && showReceipt && (
        <ReceiptScanner
          onAdd={addItems}
          onClose={() => setShowReceipt(false)}
        />
      )}

      {BACKEND_ENABLED && showPhoto && (
        <ProductPhotoCapture
          onAdd={addItems}
          onClose={() => setShowPhoto(false)}
        />
      )}
    </div>
  );
}
